import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import useSubscription from 'hooks/apollo/useSubscription';
import { useMutation } from 'hooks/apollo/useMutation';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useTranslation } from 'react-i18next';
import { addCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { CONTENT_TYPE_X_METADATA } from '../../queries';
import { DELETE_CONTENT_TYPE_X_METADATA, SAVE_CONTENT_TYPE_X_METADATA } from '../../mutations';
import { CONTENT_TYPE_X_METADATA_SAVED } from '../../subscriptions';
import { IconButton, Checkbox } from '@mui/material';
import { Delete, Edit, Save, Close, Check } from '@mui/icons-material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { contentTypeXMetadataValidator } from '../../validators';

const MetadataItemComponent = ({
  index,
  metadata,
  contentTypeXMetadataList,
  contentTypeXMetadataOptions,
  canWrite,
  onCancelAdd,
  contentTypeId
}) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const metadataValidator = useMemo(() => contentTypeXMetadataValidator(contentTypeXMetadataList), [contentTypeXMetadataList]);

  const [validation, validate] = useDirtyFieldValidation(metadataValidator);

  const [editing, setEditing] = useState(!metadata.id);
  const [pending, setPending] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(metadata);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const metadataName = useMemo(
    () => contentTypeXMetadataOptions.find(item => item.id === metadata.metadataId)?.name || '',
    [contentTypeXMetadataOptions, metadata]
  );
  const disabled = useMemo(() => pending || !canWrite, [canWrite, pending]);

  const filterContentTypeXMetadata = useCallback(
    value => {
      const inputText = value?.toString()?.toUpperCase() || '';
      if (!inputText) return Promise.resolve([]);
      const filteredContentTypeXMetadata = contentTypeXMetadataOptions?.filter(({ name }) => name.toUpperCase().includes(inputText));
      return Promise.resolve(filteredContentTypeXMetadata);
    },
    [contentTypeXMetadataOptions]
  );

  const contentTypeXMetadataValue = useMemo(
    () => contentTypeXMetadataOptions?.find(({ id }) => (lens.metadataId |> get) === id),
    [lens.metadataId, contentTypeXMetadataOptions]
  );

  const [saveContentTypeXMetadata] = useMutation(SAVE_CONTENT_TYPE_X_METADATA, {
    onError: () => {
      setPending(false);
    }
  });

  const [deleteContentTypeXMetadata] = useMutation(DELETE_CONTENT_TYPE_X_METADATA, {
    onError: () => {
      setPending(false);
    }
  });

  const handleSave = () => {
    if (!validate(lens |> get)) return;

    const input = lens |> get;
    setPending(true);
    saveContentTypeXMetadata({ variables: { input } });
  };

  const handleDelete = useCallback(
    id => {
      confirm(
        () => {
          setPending(true);
          deleteContentTypeXMetadata({ variables: { id } });
        },
        {
          content: t('Metadata.AreYouSureDeleteMetadata')
        }
      );
    },
    [confirm, t, deleteContentTypeXMetadata]
  );

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  useSubscription(CONTENT_TYPE_X_METADATA_SAVED, {
    onError: error => addToast(error, 'error'),
    variables: { id: metadata.id },
    onData: ({
      client,
      data: {
        data: {
          contentTypeXMetadataSaved: { isNew, contentTypeXMetadata }
        }
      }
    }) => {
      setEditing(false);
      setPending(false);
      if (isNew) onCancelAdd();
      addCachedDetail(
        client,
        { name: 'contentTypeXMetadata', value: CONTENT_TYPE_X_METADATA },
        { contentTypeId: contentTypeId },
        x => x.id === metadata.id,
        contentTypeXMetadata,
        ['id']
      );
      addToast(t('Metadata.MetadataSaved'), 'success');
    }
  });

  const handleCancelEdit = useCallback(() => {
    if (!metadata.id) onCancelAdd();
    else {
      setEditing(false);
      resetLens(metadata);
    }
  }, [onCancelAdd, resetLens, metadata]);

  const handleEdit = useCallback(() => {
    setEditing(true);
  }, []);

  return (
    <StyledTableRow hover key={index}>
      <StyledTableBodyCell colSpan={2}>
        {editing ? (
          <Autocomplete
            fullWidth
            value={contentTypeXMetadataValue}
            onChange={value => (lens.metadataId |> set)(value.id)}
            required
            valueKey='id'
            labelKey='name'
            loadOptions={filterContentTypeXMetadata}
            label={t('Metadata.Title')}
            error={!isValid(validation.metadataId) || !isValid(validation)}
            helperText={getErrors(validation.metadataId) || getErrors(validation)}
            disabled={disabled || !canWrite}
          />
        ) : (
          <>{metadataName}</>
        )}
      </StyledTableBodyCell>
      <StyledTableBodyCell align='right' colSpan={1}>
        {editing ? (
          <Checkbox
            disabled={!canWrite}
            checked={get(lens.mandatory) || false}
            onChange={() => set(lens.mandatory, !get(lens.mandatory))}
            error={!isValid(validation.mandatory)}
            helperText={getErrors(validation.mandatory)}
          />
        ) : (
          <>{(lens.mandatory |> get) === true ? <Check color='success' /> : <Close />}</>
        )}
      </StyledTableBodyCell>
      <StyledTableBodyCell align='right' colSpan={2}>
        {editing ? (
          <>
            <IconButton
              type='cancel'
              size='tiny'
              color='primary'
              variant='text'
              onClick={handleCancelEdit}
              tooltip={t('Cancel')}
              disabled={disabled || !canWrite}
            >
              <Close />
            </IconButton>
            <IconButton
              type='save'
              color='primary'
              size='tiny'
              variant='text'
              onClick={handleSave}
              tooltip={t('Save')}
              disabled={disabled || !isDirty}
            >
              <Save />
            </IconButton>
          </>
        ) : (
          <IconButton
            type='edit'
            color='primary'
            size='tiny'
            variant='text'
            onClick={handleEdit}
            tooltip={t('Edit')}
            disabled={disabled || !canWrite}
          >
            <Edit />
          </IconButton>
        )}
        {(lens.id |> get) && (
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => handleDelete(metadata.id)}
            tooltip={t('Delete')}
            disabled={disabled || !canWrite}
          >
            <Delete />
          </IconButton>
        )}
      </StyledTableBodyCell>
    </StyledTableRow>
  );
};

MetadataItemComponent.propTypes = {
  index: PropTypes.number,
  metadata: PropTypes.object,
  contentTypeXMetadataList: PropTypes.array,
  contentTypeXMetadataOptions: PropTypes.array,
  canWrite: PropTypes.bool,
  onCancelAdd: PropTypes.func,
  contentTypeId: PropTypes.number
};

export default MetadataItemComponent;
